// src/exercises/SpeakingExerciseFree.jsx
// ══════════════════════════════════════════════════════════════════════════════
// VERSIÓN AJUSTADA — isPausedForRescue ELIMINADO + ExerciseTokensGuard
//  ✅ SpacyPanicButtons ya no recibe isPaused
//  ✅ handleHelpButton ya no activa pausa
//  ✅ handleSendMessage simplificado — sin detección de "continuar"
//  🆕 user_level se envía al backend para ayuda calibrada
//  🛡️ Envuelto en ExerciseTokensGuard (escudo de suscripción)
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { LIMITES, avisaDelTope, motivoDelFallo } from '../limites';
import { Mic, Send, Bot, User, Volume2, Clock, RefreshCw, Eye, MessageCircle, AlertCircle, History, Maximize, Minimize, ZoomIn, ZoomOut, Loader, ArrowRight, ArrowLeft, CheckCircle, Sparkles, BookOpen, Zap, Drama, Globe, Briefcase, GraduationCap, Shield, Flame, Coffee, Stethoscope, Laptop, Flag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import './Exercise.css';
import './SpeakingExerciseFree.css';
import { launchConfetti, showAchievement } from '../utils/effects.js';
import useLiveTranscription from './useLiveTranscription.js';
import VoiceCoachStrip from './VoiceCoachStrip.jsx';

// webSpeechUtils eliminado para restaurar OpenAI Cloud
// ── NUEVO: Guard de tokens para este ejercicio ───────────────────────────────
import ExerciseTokensGuard from '../components/guards/ExerciseTokensGuard';

// Assets
// ── Video en vez de gif ─────────────────────────────────────────────────────
// Los gifs de Spacy son de 1080x1080 y pesan 44 MB entre todos; el de hablar,
// el solo, 26,5 MB. Los mismos videos a 512 px pesan 664 KB entre todos y a
// tamano de pantalla se ven igual. Un gif no comprime entre fotogramas —guarda
// cada cuadro casi entero—, un video si. Los .gif siguen en assets/ por si
// hiciera falta volver.
import spacyNormal from '../assets/spacy-normal.mp4';
import spacyListening from '../assets/spacy-escuchando.mp4';
import spacySpeaking from '../assets/spacy-hablando.mp4';
import spacyThinking from '../assets/spacy-pensando.mp4';
import spacyError from '../assets/spacy-error.mp4';

import { SpacyThinkingBubble, ScanBorderWrapper } from "../components/SpacyScanner";
import "../components/spacy-scanner.css";

import SpacyMessageRenderer from './SpacyMessageRenderer';
import './spacy-renderer.css';

// El mismo cierre que la lección, en su versión de tutor: Rob quiso que las
// dos Spacys se vieran de la misma familia (24-09-2026).
import CierreDeLeccion from './CierreDeLeccion';
import SpacyPanicButtons from './SpacyPanicButtons';
import './spacy-panic.css';

// ─── Utilidades de texto ──────────────────────────────────────────────────────

const correctCommonErrors = (text) => {
    const corrections = {
        'bola': 'hola', 'ola': 'hola', 'komo': 'cómo', 'komo estas': 'cómo estás',
        'grasias': 'gracias', 'porfavor': 'por favor', 'porke': 'porque', 'ke': 'qué',
        'kiero': 'quiero', 'anser': 'answer', 'inglis': 'inglés',
        'helo': 'hello', 'jello': 'hello', 'wat': 'what', 'wats': 'what\'s',
        'wheres': 'where\'s', 'hows': 'how\'s', 'tanks': 'thanks', 'plis': 'please',
        'plz': 'please', 'sori': 'sorry', 'becos': 'because', 'cud': 'could',
        'wud': 'would', 'shud': 'should'
    };
    let correctedText = text.toLowerCase();
    Object.keys(corrections).forEach(error => {
        const regex = new RegExp(`\\b${error}\\b`, 'gi');
        correctedText = correctedText.replace(regex, corrections[error]);
    });
    return correctedText;
};

const normalizePunctuation = (text) => {
    return text
        .replace(/\s*\.\s*/g, '. ').replace(/\s*,\s*/g, ', ')
        .replace(/\s*\?\s*/g, '? ').replace(/\s*!\s*/g, '! ')
        .replace(/\s+\./g, '.').replace(/\s+,/g, ',').trim();
};

const improveCapitalization = (text) => {
    if (!text) return text;
    let result = text.charAt(0).toUpperCase() + text.slice(1);
    result = result.replace(/([.!?])\s+(\w)/g, (match, punct, letter) => `${punct} ${letter.toUpperCase()}`);
    return result;
};

const autoPunctuate = (text) => {
    if (!text.trim()) return text;
    let correctedText = text.trim();
    const questionWords = ['what', 'how', 'why', 'when', 'where', 'who', 'which',
        'can you', 'could you', 'would you', 'do you', 'does', 'is', 'are', 'will', 'should'];
    const isQuestion = questionWords.some(word =>
        correctedText.toLowerCase().startsWith(word) ||
        correctedText.toLowerCase().includes(` ${word}`)
    );
    const lastChar = correctedText[correctedText.length - 1];
    const hasPunctuation = ['.', '?', '!', ',', ';', ':'].includes(lastChar);
    if (!hasPunctuation) {
        correctedText += isQuestion ? '?' : '.';
    }
    correctedText = correctedText.charAt(0).toUpperCase() + correctedText.slice(1);
    return correctedText;
};

// ─── Custom Hooks ─────────────────────────────────────────────────────────────

const useFontSize = (initialSize = 16) => {
    const [fontSize, setFontSize] = useState(() => {
        const saved = localStorage.getItem('spacy-chat-font-size');
        return saved ? parseInt(saved) : initialSize;
    });
    const increaseFontSize = () => setFontSize(prev => { const n = Math.min(prev + 2, 24); localStorage.setItem('spacy-chat-font-size', n); return n; });
    const decreaseFontSize = () => setFontSize(prev => { const n = Math.max(prev - 2, 14); localStorage.setItem('spacy-chat-font-size', n); return n; });
    const resetFontSize = () => { setFontSize(initialSize); localStorage.setItem('spacy-chat-font-size', initialSize); };
    return { fontSize, increaseFontSize, decreaseFontSize, resetFontSize };
};

const useAutoScroll = (dependencies = []) => {
    const chatContainerRef = useRef(null);
    const isUserScrollingRef = useRef(false);
    const scrollTimeoutRef = useRef(null);

    useLayoutEffect(() => {
        const container = chatContainerRef.current;
        if (!container) return;
        const handleUserScroll = () => {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            isUserScrollingRef.current = true;
            scrollTimeoutRef.current = setTimeout(() => { isUserScrollingRef.current = false; }, 1500);
        };
        container.addEventListener('scroll', handleUserScroll);
        return () => {
            container.removeEventListener('scroll', handleUserScroll);
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };
    }, []);

    const scrollToBottom = (behavior = 'smooth') => {
        if (isUserScrollingRef.current) return;
        const container = chatContainerRef.current;
        if (!container) return;
        container.scrollTo({ top: container.scrollHeight, behavior });
    };

    const scrollToElement = (element, behavior = 'smooth') => {
        if (isUserScrollingRef.current || !element) return;
        const container = chatContainerRef.current;
        if (!container) return;
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementTopInContainer = elementRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({ top: elementTopInContainer - containerRect.height / 2 + elementRect.height / 2, behavior });
    };

    useEffect(() => {
        if (!isUserScrollingRef.current) scrollToBottom('smooth');
    }, dependencies);

    return { chatContainerRef, scrollToBottom, scrollToElement, isUserScrolling: isUserScrollingRef.current };
};

// ─── API Functions ────────────────────────────────────────────────────────────

const fetchTutorResponse = async (lessonId, message, is_initial = false, is_restart = false, resume_scene = false) => {
    try {
        const payload = { message, is_initial, is_restart, resume_scene };
        const response = await fetch(`/api/tutor-free/chat/${lessonId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            // El motivo viaja con el fallo: sin esto, un 429 del freno por
            // minuto o una sesión caducada se leían como «error de conexión».
            // Gemelo de `SpeakingExercise.jsx`.
            const fallo = new Error(`Error en la API: ${response.statusText}`);
            fallo.status = response.status;
            try {
                const cuerpo = await response.json();
                fallo.detalle = typeof cuerpo?.detail === 'string'
                    ? cuerpo.detail : cuerpo?.detail?.message;
            } catch { /* un cuerpo vacío no cambia nada */ }
            throw fallo;
        }
        return await response.json();
    } catch (error) {
        console.error("No se pudo obtener la respuesta del tutor:", error);
        return { reply: "Lo siento, tuve un problema de conexión. ¿Podemos intentarlo de nuevo?", current_topic: "Error" };
    }
};

const startTutorSession = async (lessonId, setupData) => {
    try {
        const payload = {
            topic: setupData.topic,
            user_level: setupData.user_level,
            language_mode: setupData.language_mode,
            depth_mode: setupData.depth_mode,
            display_info: setupData.display_info
        };
        const response = await fetch(`/api/tutor-free/start-session/${lessonId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            // El motivo viaja con el fallo: sin esto, un 429 del freno por
            // minuto o una sesión caducada se leían como «error de conexión».
            // Gemelo de `SpeakingExercise.jsx`.
            const fallo = new Error(`Error al iniciar sesión: ${response.statusText}`);
            fallo.status = response.status;
            try {
                const cuerpo = await response.json();
                fallo.detalle = typeof cuerpo?.detail === 'string'
                    ? cuerpo.detail : cuerpo?.detail?.message;
            } catch { /* un cuerpo vacío no cambia nada */ }
            throw fallo;
        }
        return await response.json();
    } catch (error) {
        console.error("Critical Error starting session:", error);
        throw error;
    }
};

const fetchChatHistory = async (lessonId) => {
    try {
        const response = await fetch(`/api/tutor-free/chat/${lessonId}/history`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) throw new Error(`Error en la API de historial: ${response.statusText}`);
        const data = await response.json();
        return data.history.map(msg => {
            const historyData = msg.parts[0];
            let messageText = '';
            if (msg.role === 'model') {
                try { messageText = JSON.parse(historyData).reply; } catch (e) { messageText = historyData; }
            } else {
                messageText = historyData;
            }
            return { speaker: msg.role === 'user' ? 'Student' : 'Spacy', text: messageText };
        });
    } catch (error) {
        console.error("No se pudo obtener el historial del chat:", error);
        return [];
    }
};

const fetchFreshCards = async (lessonId) => {
    try {
        const response = await fetch(`/api/tutor-free/lesson-details/${lessonId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) return [];
        const data = await response.json();
        return data.visual_gallery_cards || [];
    } catch (e) { return []; }
};

const fetchAudioBlob = async (textToSpeak) => {
    if (!textToSpeak) return null;
    let textForAudio = textToSpeak;
    if (textForAudio.includes('---')) textForAudio = textForAudio.split('---')[0].trim();
    try {
        const response = await fetch('/api/tutor-free/speak', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ text: textForAudio })
        });
        if (!response.ok) return null;
        return await response.blob();
    } catch (e) { console.error("Error fetching audio blob", e); return null; }
};

const transcribeAudioWithWhisper = async (audioBlob) => {
    if (!audioBlob) return '';

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 35000); // 35s máximo

    try {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');
        const response = await fetch('/api/tutor-free/transcribe', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: formData,
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        if (!response.ok) {
            console.error(`Transcribe error: ${response.status}`);
            return '';
        }
        const data = await response.json();
        return data.text || '';
    } catch (e) {
        clearTimeout(timeoutId);
        if (e.name === 'AbortError') {
            console.error('Transcription timeout after 35s');
        } else {
            console.error("Error transcribing audio with Whisper", e);
        }
        return '';
    }
};

// ─── SpacyLaunchpad ───────────────────────────────────────────────────────────

function SpacyLaunchpad({ onLaunch, userLevel, defaultTopic }) {
    const [englishLevel, setEnglishLevel] = useState('intermediate');
    const [context, setContext] = useState('');
    const [supportMode, setSupportMode] = useState('assisted');

    const handleStart = () => {
        const baseTopic = defaultTopic || "Conversación Libre";
        const finalPrompt = context.trim() ? `${baseTopic}: ${context.trim()}` : baseTopic;
        onLaunch({
            topic: finalPrompt,
            user_level: englishLevel,
            language_mode: supportMode,
            depth_mode: "creative_lab",
            display_info: {
                branch: supportMode === 'immersion' ? 'hardcore' : 'assisted',
                formatted_topic: finalPrompt,
                user_context: context.trim() || 'Libre'
            }
        });
    };

    return (
        <div className="sef-launchpad-container fade-in">
            <div className="sef-launchpad-card">
                <div className="sef-lp-header">
                    <div className="sef-lp-icon-wrapper">🚀</div>
                    <div className="sef-lp-title-group">
                        <h3>{defaultTopic || "Conversación Libre"}</h3>
                        <p className="sef-lp-subtitle">Deja que Spacy improvise o dale instrucciones</p>
                    </div>
                </div>

                <div className="sef-lp-section">
                    <label className="sef-lp-label">1. ¿En qué nivel te sientes?</label>
                    <div className="sef-level-selector">
                        {['basic', 'intermediate', 'advanced'].map(level => (
                            <button
                                key={level}
                                className={`sef-level-btn ${englishLevel === level ? 'active' : ''}`}
                                onClick={() => setEnglishLevel(level)}
                            >
                                <span className="level-emoji">
                                    {level === 'basic' ? '🌱' : level === 'intermediate' ? '🚀' : '⚡'}
                                </span>
                                <span className="level-text">
                                    {level.charAt(0).toUpperCase() + level.slice(1)}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="sef-lp-section">
                    <label className="sef-lp-label">2. ¿Qué quieres practicar? (Opcional)</label>
                    <div className="sef-textarea-wrapper">
                        <textarea
                            maxLength={LIMITES.contexto}
                            className="sef-lp-textarea"
                            placeholder="Ej: 'Ordenar comida en un restaurante', 'Entrevista de trabajo'... o déjalo vacío para que Spacy improvise"
                            value={context}
                            onChange={(e) => setContext(e.target.value)}
                            rows={3}
                        />
                    </div>
                    <p className="sef-hint-text">💡 Mientras más específico seas, mejor Spacy improvisará el rol</p>
                </div>

                <div className="sef-lp-section">
                    <label className="sef-lp-label">3. Nivel de Desafío</label>
                    <div className="sef-support-switch">
                        <button
                            className={`sef-support-opt ${supportMode === 'assisted' ? 'active' : ''}`}
                            onClick={() => setSupportMode('assisted')}
                        >
                            <Shield size={24} className="icon-shield" />
                            <div className="sef-support-text">
                                <span className="title">Asistido</span>
                                <span className="desc">🇺🇸 Inglés + 🇪🇸 Ayuda</span>
                            </div>
                            {supportMode === 'assisted' && <div className="check-indicator">●</div>}
                        </button>
                        <button
                            className={`sef-support-opt ${supportMode === 'immersion' ? 'active-fire' : ''}`}
                            onClick={() => setSupportMode('immersion')}
                        >
                            <Flame size={24} className="icon-fire" />
                            <div className="sef-support-text">
                                <span className="title">Realidad</span>
                                <span className="desc">🇺🇸 Solo Inglés 🔥</span>
                            </div>
                            {supportMode === 'immersion' && <div className="check-indicator">●</div>}
                        </button>
                    </div>
                </div>

                <button className="sef-launch-btn" onClick={handleStart}>
                    <Sparkles size={18} />
                    DESPEGAR
                </button>
            </div>
            <div className="sef-branding">🅢🅟🅐🅒🅨 🧪 LAB</div>
        </div>
    );
}

// ─── Componente Tarjeta Sistema ───────────────────────────────────────────────

const ChatKeyPointCard = ({ data }) => {
    let card = {};
    try { card = typeof data === 'string' ? JSON.parse(data) : data; } catch (e) { return null; }
    return (
        <div className="sef-chat-keypoint fade-in-up">
            <div className="sef-keypoint-icon-wrapper">💡</div>
            <div className="sef-keypoint-content">
                <h4>{card.title || "Tip"}</h4>
                <p>{card.content}</p>
            </div>
        </div>
    );
};

// ─── Sección colapsable español ───────────────────────────────────────────────

const CollapsibleSpanishSection = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="sef-spanish-collapsible">
            <button
                className="sef-spanish-toggle"
                onClick={() => setIsOpen(!isOpen)}
                type="button"
            >
                <Globe size={14} style={{ marginRight: '6px' }} />
                <span>{isOpen ? 'Ocultar traducción' : 'Ver traducción al español'}</span>
                <span className="toggle-icon" style={{ marginLeft: 'auto' }}>{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
                <div className="sef-spanish-content fade-in-up">
                    {children}
                </div>
            )}
        </div>
    );
};

const renderSpacyBubble = (text) => {
    const sepIdx = text.search(/\n\s*---\s*\n/);
    if (sepIdx === -1) return <SpacyMessageRenderer text={text} />;
    const eng = text.slice(0, sepIdx).trim();
    const esp = text.slice(sepIdx).replace(/^\s*---\s*\n/, '').trim();
    return (
        <React.Fragment>
            <SpacyMessageRenderer text={eng} />
            <CollapsibleSpanishSection>
                <SpacyMessageRenderer text={esp} />
            </CollapsibleSpanishSection>
        </React.Fragment>
    );
};

// ─── Componente Principal ─────────────────────────────────────────────────────

function SpeakingExerciseFree({ exerciseId, exerciseData, onCorrectAnswer }) {
    const { title, learning_objectives, visual_gallery_cards: initialCards, user_level } = exerciseData?.data || {};
    const [cards, setCards] = useState(initialCards || []);

    // Estados principales
    const [conversation, setConversation] = useState([]);
    const [userInput, _setUserInput] = useState('');

    // ── EL TOPE, EN EL UNICO SITIO POR EL QUE PASAN TODOS ────────────────────
    // El `maxLength` de la caja frena al alumno cuando ESCRIBE o PEGA, y con eso
    // basta para el uso normal. Pero no actua cuando el texto se mete por
    // programa, y aqui pasa en tres sitios: la transcripcion de la voz, el
    // dictado en vivo y el texto "mejorado". Por ahi cabia un mensaje de
    // cualquier tamano, que ademas no da error hasta que se pulsa enviar.
    //
    // Se recorta en el propio setter para que ningun camino —ni los que se
    // escriban manana— pueda saltarselo. Un tope repartido por siete llamadas
    // es un tope que alguien se va a dejar.
    const setUserInput = useCallback((v) => {
        _setUserInput((anterior) => {
            const siguiente = typeof v === 'function' ? v(anterior) : v;
            return typeof siguiente === 'string'
                ? siguiente.slice(0, LIMITES.mensajeSpacy)
                : siguiente;
        });
    }, []);

    const [isSpacyThinking, setIsSpacyThinking] = useState(false);
    const [spacyState, setSpacyState] = useState('neutral');
    const [currentTopic, setCurrentTopic] = useState('');
    const [isCompleted, setIsCompleted] = useState(false);
    const [isSpacyMode, setIsSpacyMode] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [isActive, setIsActive] = useState(false);
    const [stats, setStats] = useState({ words: 0, phrases: 0, accuracy: 100 });
    // ── EL FINAL DEL TUTOR LO DECIDE EL ALUMNO ──────────────────────────────
    // La lección la cierra Spacy cuando da el tema por dominado. Aquí no hay
    // tema: es conversación libre, así que el final es un botón. Sin él, la
    // sesión no terminaba nunca — se abandonaba.
    const [sesionCerrada, setSesionCerrada] = useState(false);
    const [showHistoryButton, setShowHistoryButton] = useState(false);
    const [isHighlightingEnabled, setIsHighlightingEnabled] = useState(true);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [longWait, setLongWait] = useState(false);

    // Audio
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [voiceRecognitionError, setVoiceRecognitionError] = useState('');

    // ── OIDO EN VIVO ──────────────────────────────────────────────────────────
    // Exactamente el mismo hook que el Spacy del curso, contra el mismo
    // WebSocket: el tutor libre pasa por el mismo guardia `ActiveUser`, asi que
    // no hace falta ni una linea de backend nueva. Un solo sitio donde
    // arreglarlo si algo falla, y un solo sitio que probar.
    const live = useLiveTranscription();
    const [voiceMetrics, setVoiceMetrics] = useState(null);
    const [rawTranscript, setRawTranscript] = useState('');

    // La celebracion espera a que Spacy termine de hablar. Es un ref y no un
    // estado porque quien lo lee es el `onended` del audio, creado una sola vez.
    const victoriaPendienteRef = useRef(false);

    const currentAudioRef = useRef(null);

    // Selección y persistencia
    const [showSelectionCard, setShowSelectionCard] = useState(null);
    const [currentConfig, setCurrentConfig] = useState(null);

    // Refs
    const { fontSize, increaseFontSize, decreaseFontSize, resetFontSize } = useFontSize();
    const { chatContainerRef, scrollToBottom, scrollToElement } = useAutoScroll([conversation.length, isSpacyThinking]);
    const conversationContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const isSpeakingRef = useRef(false);
    const stepsContainerRef = useRef(null);
    const mainCardRef = useRef(null);
    const lastSpokenMessageIdRef = useRef(null);
    const initializedIdRef = useRef(null);
    // browserRecognitionRef eliminado (usando Whisper Cloud)

    const spacyGifMap = {
        neutral: spacyNormal, listening: spacyListening,
        speaking: spacySpeaking, thinking: spacyThinking, error: spacyError
    };

    // ── Efecto de paciencia y auto-scroll ────────────────────────────────────
    useEffect(() => {
        let timerWait, timerScroll;
        if (isSpacyThinking) {
            timerScroll = setTimeout(() => { if (typeof scrollToBottom === 'function') scrollToBottom('smooth'); }, 100);
            timerWait = setTimeout(() => setLongWait(true), 4000);
        } else {
            setLongWait(false);
        }
        return () => { clearTimeout(timerWait); clearTimeout(timerScroll); };
    }, [isSpacyThinking, scrollToBottom]);

    // ── Inicialización ───────────────────────────────────────────────────────
    useEffect(() => {
        const initializeSession = async () => {
            if (!exerciseId) return;

            fetchFreshCards(exerciseId).then(freshCards => {
                if (freshCards && freshCards.length > 0) setCards(freshCards);
            });

            let savedConfig = null;
            try {
                const savedConfigJSON = localStorage.getItem(`spacy_free_config_${String(exerciseId)}`);
                if (savedConfigJSON) {
                    savedConfig = JSON.parse(savedConfigJSON);
                    setCurrentConfig(savedConfig);
                }
            } catch (e) { console.error("Error leyendo localStorage:", e); }

            if (showSelectionCard === false) return;

            const history = await fetchChatHistory(exerciseId);
            if (history && history.length > 0) {
                setConversation(history);
                setShowSelectionCard(false);
                setShowHistoryButton(false);
                setSpacyState('neutral');
                lastSpokenMessageIdRef.current = `msg-${history.length - 1}`;
            } else {
                setShowSelectionCard(true);
            }
        };
        initializeSession();
    }, [exerciseId]);

    useEffect(() => {
        const gifUrlsToPreload = [spacyNormal, spacyListening, spacySpeaking, spacyThinking, spacyError];
        gifUrlsToPreload.forEach(url => { const img = new Image(); img.src = url; });
    }, []);

    // ── Limpieza ─────────────────────────────────────────────────────────────
    useEffect(() => {
        return () => {
            if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
        };
    }, []);

    // ── Reproducción de respuesta ─────────────────────────────────────────────
    // Celebra la leccion, si estaba pendiente. Se llama desde TODOS los finales
    // posibles del audio —terminado, error, autoplay bloqueado, o no haberlo—
    // para que la clase nunca se quede sin cerrar por un fallo de sonido.
    const cerrarLeccionSiTocaba = () => {
        if (!victoriaPendienteRef.current) return;
        victoriaPendienteRef.current = false;
        setTimeout(() => {
            launchConfetti();
            showAchievement('¡Lección completada!', '🎉');
        }, 450);
    };

    const processAndPlayResponse = async (responseText, additionalMessages = []) => {
        const audioBlob = await fetchAudioBlob(responseText);
        setIsSpacyThinking(false);
        const spacyMsg = { speaker: 'Spacy', text: responseText };
        setConversation(prev => [...prev, spacyMsg, ...additionalMessages]);

        if (audioBlob) {
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            currentAudioRef.current = audio;
            setSpacyState('speaking');
            isSpeakingRef.current = true;
            setIsSpeaking(true);
            audio.onended = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                setSpacyState('neutral');
                URL.revokeObjectURL(audioUrl);
                cerrarLeccionSiTocaba();
            };
            audio.onerror = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                setSpacyState('error');
                cerrarLeccionSiTocaba();
            };
            try {
                await audio.play();
            } catch (err) {
                setSpacyState('neutral');
                cerrarLeccionSiTocaba();
            }
        } else {
            setSpacyState('neutral');
            cerrarLeccionSiTocaba();
        }
    };

    // ── Grabación de voz ──────────────────────────────────────────────────────
    // El camino de siempre, intacto: subir el audio completo a /transcribe.
    // Es la red de seguridad cuando el streaming no llega a abrir.
    const procesarAudioPorLotes = async (audioBlob) => {
        setIsTranscribing(true);
        setSpacyState('thinking');
        try {
            const text = await transcribeAudioWithWhisper(audioBlob);

            // 🔒 VALIDACIÓN DE ERRORES DEL BACKEND:
            if (text && text.includes("Error_")) {
                setVoiceRecognitionError("No pudimos escuchar bien. Intenta acercarte al micrófono.");
                setSpacyState('error');
                return;
            }

            if (text && text.trim().length > 0) {
                const improvedText = improveCapitalization(normalizePunctuation(correctCommonErrors(text)));
                setUserInput(prev => prev ? `${prev} ${improvedText}` : improvedText);
                setSpacyState('neutral');
                if (textareaRef.current) setTimeout(() => autoGrow(textareaRef.current), 0);
            } else {
                setSpacyState('neutral');
            }
        } catch (error) {
            console.error("Error de red en transcripción:", error);
            setVoiceRecognitionError("Error de conexión. Verifica tu red.");
            setSpacyState('error');
        } finally {
            // 🔒 EL ANTÍDOTO OBLIGATORIO:
            setIsTranscribing(false);
        }
    };

    const startRecording = async () => {
        setVoiceRecognitionError('');
        if (isSpeakingRef.current) {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current = null;
            }
            isSpeakingRef.current = false;
            setIsSpeaking(false);
        }

        // Lo del turno anterior se retira: esas métricas no dicen nada del
        // turno que viene.
        setVoiceMetrics(null);
        setRawTranscript('');

        try {
            await live.start();
            setIsRecording(true);
            setSpacyState('listening');
        } catch (err) {
            console.error("Error accessing microphone:", err);
            setVoiceRecognitionError("No se pudo acceder al micrófono.");
        }
    };

    const stopRecording = async () => {
        setIsRecording(false);
        setIsTranscribing(true);
        setSpacyState('thinking');

        let resultado;
        try {
            resultado = await live.stop();
        } catch (e) {
            console.error('Fallo al cerrar el oído en vivo:', e);
            resultado = null;
        }

        // ── CAMINO A: el streaming dio texto ──
        // No se sube el audio: Deepgram ya lo escuchó y ya se pagó. Subirlo
        // además sería pagar dos veces el mismo minuto por el mismo resultado.
        if (resultado?.texto) {
            const mejorado = improveCapitalization(normalizePunctuation(correctCommonErrors(resultado.texto)));
            setRawTranscript(mejorado);
            setVoiceMetrics(resultado.metricas || null);
            setUserInput(prev => prev ? `${prev} ${mejorado}` : mejorado);
            setIsTranscribing(false);
            setSpacyState('neutral');
            if (textareaRef.current) setTimeout(() => autoGrow(textareaRef.current), 0);
            return;
        }

        // ── CAMINO B: el de siempre ──
        if (resultado?.fallbackBlob) {
            await procesarAudioPorLotes(resultado.fallbackBlob);
            return;
        }

        setIsTranscribing(false);
        setSpacyState('neutral');
    };

    const handleToggleListening = () => isRecording ? stopRecording() : startRecording();

    // ── Inicializar conversación ──────────────────────────────────────────────
    const initializeConversation = async (isRestart = false, setupPayload = null) => {
        if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
        setIsSpeaking(false);
        isSpeakingRef.current = false;
        initializedIdRef.current = exerciseId;
        lastSpokenMessageIdRef.current = null;
        setConversation([]);
        setCurrentTopic('');
        setIsCompleted(false);
        setTimeLeft(0);
        setIsActive(false);
        setStats({ words: 0, phrases: 0, accuracy: 100 });
        setShowHistoryButton(false);
        setIsSpacyThinking(true);
        setSpacyState('thinking');

        try {
            let initialResponse;

            if (setupPayload) {
                localStorage.setItem(`spacy_free_config_${String(exerciseId)}`, JSON.stringify(setupPayload));
                setCurrentConfig(setupPayload);
                const sessionData = await startTutorSession(exerciseId, setupPayload);
                initialResponse = { reply: sessionData.message, current_topic: setupPayload.topic, has_history: false };
            } else {
                initialResponse = await fetchTutorResponse(exerciseId, "", true, isRestart);
            }

            setCurrentTopic(initialResponse.current_topic);
            if (initialResponse.has_history) setShowHistoryButton(true);
            await processAndPlayResponse(initialResponse.reply);

        } catch (error) {
            console.error("Error inicializando:", error);
            // Gemelo del de `SpeakingExercise.jsx`: el mensaje dice QUÉ pasó y
            // qué hacer. Sin `status`, sí fue la red de verdad.
            const motivo = error?.status
                ? (error.detalle || motivoDelFallo(error.status))
                : 'No pude conectar. Revisa tu internet y vuelve a intentarlo.';
            setConversation([{ speaker: 'Spacy', text: `No pude arrancar la clase. ${motivo}` }]);
            setIsSpacyThinking(false);
            setSpacyState('neutral');
        }
    };

    const handleLaunchpadComplete = (config) => {
        setShowSelectionCard(false);
        initializeConversation(false, config);
    };

    useEffect(() => {
        if (exerciseId && initializedIdRef.current !== exerciseId && showSelectionCard === false && conversation.length === 0) {
            initializeConversation(false, currentConfig);
        }
    }, [exerciseId, exerciseData, showSelectionCard]);

    useEffect(() => {
        if (currentTopic && stepsContainerRef.current) {
            const activeStepElement = stepsContainerRef.current.querySelector('.sef-lesson-step.active');
            if (activeStepElement) activeStepElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [currentTopic]);

    // ── Speak manual ──────────────────────────────────────────────────────────
    const speakManual = async (textToSpeak, _messageId) => {
        if (!textToSpeak) return;
        if (isSpeakingRef.current) {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current = null;
            }
            isSpeakingRef.current = false;
            setIsSpeaking(false);
        }
        const audioBlob = await fetchAudioBlob(textToSpeak);
        if (audioBlob) {
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            currentAudioRef.current = audio;
            setSpacyState('speaking');
            isSpeakingRef.current = true;
            setIsSpeaking(true);
            audio.onended = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                setSpacyState('neutral');
                URL.revokeObjectURL(audioUrl);
            };
            audio.onerror = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                setSpacyState('error');
            };
            try {
                await audio.play();
            } catch (err) {
                setSpacyState('neutral');
            }
        } else {
            setSpacyState('neutral');
        }
    };

    useEffect(() => { if (isSpeaking && spacyState !== 'speaking') setSpacyState('speaking'); }, [isSpeaking]);
    useEffect(() => {
        if (!isSpeaking && spacyState === 'speaking') {
            const id = setTimeout(() => setSpacyState('neutral'), 300);
            return () => clearTimeout(id);
        }
        if (!isSpeaking && spacyState === 'error') {
            const id = setTimeout(() => setSpacyState('neutral'), 1000);
            return () => clearTimeout(id);
        }
    }, [isSpeaking, spacyState]);

    // ── ENVIAR MENSAJE (SIMPLIFICADO) ────────────────────────────────────────
    const handleSendMessage = async () => {
        let userText = userInput.trim();
        if (!userText || isSpacyThinking) return;

        userText = autoPunctuate(userText);
        if (!isActive) setIsActive(true);

        if (isSpeakingRef.current) {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current = null;
            }
            isSpeakingRef.current = false;
            setIsSpeaking(false);
            setSpacyState('neutral');
        }
        // El alumno pulso enviar con el micro abierto: se descarta lo que
        // estuviera grabando. Si se parara la grabacion, su transcrito
        // aterrizaria en el textarea DESPUES de haberlo vaciado al enviar.
        if (isRecording) { live.cancel(); setIsRecording(false); }
        setRawTranscript('');
        setVoiceMetrics(null);

        const newUserMessage = { speaker: 'Student', text: userText };
        setConversation(prev => [...prev, newUserMessage]);
        setStats(prev => ({ ...prev, words: prev.words + userText.split(/\s+/).length, phrases: prev.phrases + 1 }));
        setUserInput('');
        if (textareaRef.current) { textareaRef.current.style.height = 'auto'; textareaRef.current.style.height = '40px'; }

        setIsSpacyThinking(true);
        setSpacyState('thinking');

        try {
            // Llamada sin resume_scene
            const response = await fetchTutorResponse(exerciseId, userText);

            let extraMessages = [];
            if (response.unlock_gallery_card_id && cards?.length > 0) {
                const cardFound = cards.find(c => c.card_id === response.unlock_gallery_card_id);
                if (cardFound) {
                    extraMessages.push({
                        speaker: 'System-Card',
                        text: JSON.stringify({ title: cardFound.title || cardFound.trigger_topic, content: cardFound.content })
                    });
                }
            }

            setCurrentTopic(response.current_topic);

            if (response.current_topic === "Lección Completada" && !isCompleted) {
                setIsCompleted(true);
                if (onCorrectAnswer) onCorrectAnswer();
                // El confeti NO salta aqui: Spacy todavia tiene que decir su
                // mensaje de cierre. Saltaba a la vez y el alumno no sabia si la
                // clase habia terminado o seguia. Ver `cerrarLeccionSiTocaba`.
                victoriaPendienteRef.current = true;
            }

            await processAndPlayResponse(response.reply, extraMessages);

        } catch (error) {
            console.error("Error en la conversación con el tutor:", error);
            setConversation(prev => [...prev, { speaker: 'Spacy', text: "Lo siento, tuve un problema. ¿Podemos intentarlo de nuevo?" }]);
            setSpacyState('error');
            setIsSpacyThinking(false);
        }
    };

    const handleLoadHistory = async () => {
        setShowHistoryButton(false);
        const history = await fetchChatHistory(exerciseId);
        if (history.length > 0) setConversation(prev => [...history, ...prev.slice(1)]);
    };

    // ── BOTONES DE AYUDA (SIN PAUSA) ─────────────────────────────────────────
    const handleHelpButton = async (type) => {
        if (isSpacyThinking) return;

        setIsSpacyThinking(true);
        setSpacyState('thinking');

        // Construir historial para el backend (sin mensajes internos)
        const historyForAI = conversation
            .filter(msg => msg.speaker !== 'System-Card')
            .map(msg => ({
                role: msg.speaker === 'Student' ? 'user' : 'model',
                parts: [msg.text]
            }));

        const activeTopic = currentTopic || title || 'Conversación Libre';

        // Nivel del alumno desde la config guardada en el Launchpad
        const studentLevel = currentConfig?.user_level || 'intermediate';

        try {
            const response = await fetch(`/api/tutor-free/help/${exerciseId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    help_type: type,
                    current_topic: activeTopic,
                    conversation_history: historyForAI,
                    user_level: studentLevel,   // nivel calibrado
                })
            });

            if (!response.ok) throw new Error('Help request failed');
            const data = await response.json();

            setConversation(prev => [...prev, {
                speaker: 'Spacy-Help',
                text: data.reply,
                helpType: type
            }]);

        } catch (error) {
            console.error("Error en ayuda free:", error);
            setConversation(prev => [...prev, {
                speaker: 'Spacy-Help',
                text: "No pude generar la ayuda. Intenta de nuevo."
            }]);
        } finally {
            setIsSpacyThinking(false);
            if (spacyState === 'thinking') setSpacyState('neutral');
        }
    };

    // ── Reiniciar ─────────────────────────────────────────────────────────────
    const restartExercise = async () => {
        if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
        // El alumno pulso enviar con el micro abierto: se descarta lo que
        // estuviera grabando. Si se parara la grabacion, su transcrito
        // aterrizaria en el textarea DESPUES de haberlo vaciado al enviar.
        if (isRecording) { live.cancel(); setIsRecording(false); }
        setRawTranscript('');
        setVoiceMetrics(null);
        setConversation([]);
        setUserInput('');
        setIsSpacyThinking(false);
        setSpacyState('neutral');
        setCurrentTopic('');
        setIsCompleted(false);
        setIsRecording(false);
        setIsTranscribing(false);
        setTimeLeft(0);
        setIsActive(false);
        setStats({ words: 0, phrases: 0, accuracy: 100 });
        setShowHistoryButton(false);
        setIsHighlightingEnabled(true);
        setIsSpeaking(false);
        setVoiceRecognitionError('');

        try { await fetchTutorResponse(exerciseId, "", false, true); } catch (error) { console.error("Error al enviar señal de reinicio:", error); }

        localStorage.removeItem(`spacy_free_config_${exerciseId}`);
        setCurrentConfig(null);
        setShowSelectionCard(true);
        initializedIdRef.current = null;
        lastSpokenMessageIdRef.current = null;
    };

    const handleToggleFullscreen = () => setIsFullscreen(prev => !prev);
    const toggleHighlighting = () => setIsHighlightingEnabled(prev => !prev);
    const toggleSpacyMode = () => setIsSpacyMode(prev => !prev);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const autoGrow = (element) => {
        if (element) {
            element.style.height = 'auto';
            element.style.height = (element.value === '') ? '40px' : element.scrollHeight + 'px';
        }
    };

    useEffect(() => {
        let interval = null;
        if (isActive && !isCompleted) { interval = setInterval(() => setTimeLeft(prevTime => prevTime + 1), 1000); }
        else if (!isActive && timeLeft !== 0) { clearInterval(interval); }
        return () => clearInterval(interval);
    }, [isActive, isCompleted, timeLeft]);

    const getChatStyle = () => ({ fontSize: `${fontSize}px`, lineHeight: `${fontSize * 1.4}px` });
    const getInputStyle = () => ({ fontSize: `${fontSize}px`, lineHeight: `${fontSize * 1.4}px` });
    const getBubbleStyle = () => ({ fontSize: `${fontSize}px`, lineHeight: `${fontSize * 1.4}px` });

    // ── Loading state ─────────────────────────────────────────────────────────
    if (showSelectionCard === null) {
        return (
            <div className="exercise-container sef-loading">
                <Loader className="spin-animation" size={40} />
                <p style={{ marginTop: 10, color: '#666', fontSize: 14 }}>Preparando laboratorio...</p>
            </div>
        );
    }

    // ── Render principal (envuelto en ExerciseTokensGuard) ────────────────────
    return (
        <ExerciseTokensGuard>
            <div className={`exercise-container sef-exercise-container ${isSpacyMode ? 'spacy-mode-active' : ''} ${isFullscreen ? 'fullscreen-active' : ''}`}>

                {/* HEADER */}
                <header className="sef-header">
                    <div className="sef-icon-container-inset"><MessageCircle size={20} /></div>
                    <div className="sef-title-group">
                        <h2 className="main-title">{title || 'Laboratorio Creativo'}</h2>
                        <p className="main-subtitle">Practica tu inglés libremente</p>

                        {!showSelectionCard && currentConfig && (
                            <div className="sef-format-badge-wrapper fade-in">
                                <div className="sef-format-badge">
                                    {currentConfig.display_info?.branch === 'hardcore' ? '🔥 Realidad' : '🛡️ Asistido'}
                                </div>
                                <button className="sef-format-change-btn" onClick={restartExercise} aria-label="Cambiar formato">
                                    <RefreshCw size={14} /><span>Reset</span>
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="sef-controls">
                        <div className="sef-font-controls-header">
                            <button className="sef-control-btn" onClick={decreaseFontSize} disabled={fontSize <= 14}><ZoomOut size={16} /></button>
                            <div className="sef-font-size-display">{fontSize}px</div>
                            <button className="sef-control-btn" onClick={increaseFontSize} disabled={fontSize >= 24}><ZoomIn size={16} /></button>
                            {fontSize !== 16 && <button className="sef-control-btn reset" onClick={resetFontSize}><RefreshCw size={14} /></button>}
                        </div>
                        <div className="sef-main-controls">
                            <div className="sef-stat-box-inset"><Clock size={18} /> {formatTime(timeLeft)}</div>
                            <button className={`sef-control-btn ${!isHighlightingEnabled ? 'active' : ''}`} onClick={toggleHighlighting}><Eye size={18} /></button>
                            <button className="sef-control-btn" onClick={restartExercise}><RefreshCw size={18} /></button>
                        </div>
                    </div>
                </header>

                {/* PASOS Y ESTADÍSTICAS */}
                {!showSelectionCard && (
                    <>
                        <div className="sef-lesson-steps" ref={stepsContainerRef}>
                            {learning_objectives && learning_objectives.map((objective, index) => {
                                const currentTopicIndex = learning_objectives.indexOf(currentTopic);
                                const isCompletedStep = currentTopicIndex > index;
                                const isActiveStep = objective === currentTopic;
                                return (
                                    <div key={index} className={`sef-lesson-step ${isActiveStep ? 'active' : ''} ${isCompletedStep ? 'completed' : ''}`}>
                                        <div className="sef-step-dot"></div>
                                        <span className="sef-step-text">{objective}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="sef-stats-bar">
                            <div className="sef-stat-box-inset large"><div className="sef-stat-number">{stats.words}</div><div className="sef-stat-label">Palabras</div></div>
                            <div className="sef-stat-box-inset large"><div className="sef-stat-number">{stats.phrases}</div><div className="sef-stat-label">Frases</div></div>
                            <div className="sef-stat-box-inset large"><div className="sef-stat-number">{stats.accuracy}%</div><div className="sef-stat-label">Precisión</div></div>
                        </div>
                    </>
                )}

                {/* MAIN CARD */}
                <main className="sef-main-card-wrapper" ref={mainCardRef}>

                    {!showSelectionCard && (
                        <div className="sef-avatar-wrapper">
                            <div className={`sef-avatar ${spacyState}`}>
                                <video
                                    key={spacyGifMap[spacyState]}
                                    src={spacyGifMap[spacyState]}
                                    className="sef-spacy-gif"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    aria-label={`Spacy is ${spacyState}`}
                                />
                            </div>
                        </div>
                    )}

                    {showSelectionCard && (
                        <SpacyLaunchpad
                            onLaunch={handleLaunchpadComplete}
                            userLevel={user_level}
                            defaultTopic={title === "Conversación Libre" ? null : title}
                        />
                    )}

                    {!showSelectionCard && (
                        <>
                            <div className="sef-top-controls-compact">
                                <div className="sef-compact-controls-group">
                                    <button
                                        className={`sef-spacy-mode-toggle-compact ${isSpacyMode ? 'active' : ''}`}
                                        onClick={toggleSpacyMode}
                                        aria-label="Alternar Modo Spacy"
                                    >
                                        <Bot size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* CHAT */}
                            <ScanBorderWrapper isActive={isSpacyThinking}>
                                <div className="sef-conversation-history-container" ref={conversationContainerRef}>
                                    <div className="sef-chat-messages" ref={chatContainerRef} style={getChatStyle()}>

                                        {showHistoryButton && (
                                            <div className="sef-history-loader">
                                                <button onClick={handleLoadHistory}><History size={16} /> Ver historial anterior</button>
                                            </div>
                                        )}

                                        {conversation.map((msg, index) => {
                                            if (msg.speaker === 'System-Card') {
                                                return (
                                                    <div key={index} className="sef-chat-system-message">
                                                        <ChatKeyPointCard data={msg.text} />
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div key={index} id={`chat-message-${index}`} className={`sef-chat-bubble-wrapper ${msg.speaker.toLowerCase()} ${msg.speaker === 'Spacy-Help' ? 'help' : ''}`}>
                                                    <div className="sef-avatar-icon">
                                                        {msg.speaker === 'Spacy' && <Bot size={20} />}
                                                        {msg.speaker === 'Student' && <User size={20} />}
                                                        {msg.speaker === 'Spacy-Help' && <AlertCircle size={20} />}
                                                    </div>
                                                    <div className="sef-chat-bubble" style={getBubbleStyle()}>
                                                        {(msg.speaker === 'Spacy' || msg.speaker === 'Spacy-Help')
                                                            ? renderSpacyBubble(msg.text)
                                                            : (
                                                                <ReactMarkdown components={{
                                                                    strong: ({ node, ...props }) => <strong className="sef-highlight-text" {...props} />,
                                                                    p: ({ node, ...props }) => <p className="sef-chat-paragraph-student" {...props} />
                                                                }}>
                                                                    {msg.text}
                                                                </ReactMarkdown>
                                                            )
                                                        }
                                                        {msg.speaker === 'Spacy' && (
                                                            <button className="sef-speak-bubble-btn" onClick={() => speakManual(msg.text, index)} aria-label="Escuchar de nuevo">
                                                                <Volume2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {isSpacyThinking && (
                                            <SpacyThinkingBubble
                                                isActive={isSpacyThinking}
                                                mode="free"
                                                avatarIcon={<Bot size={20} />}
                                            />
                                        )}

                                    
                                        {/* El cierre, como último mensaje de la sesión. */}
                                        {sesionCerrada && (
                                            <CierreDeLeccion
                                                modo="tutor"
                                                lessonId={exerciseId}
                                                tema={currentConfig?.display_info?.formatted_topic || title}
                                                stats={stats}
                                                config={currentConfig}
                                                onRepetir={() => { setSesionCerrada(false); initializeConversation(false, currentConfig); }}
                                                onCambiarEscenario={() => { setSesionCerrada(false); restartExercise(); }}
                                                onSalir={() => { window.location.href = '/home'; }}
                                            />
                                        )}
</div>
                                </div>
                            </ScanBorderWrapper>

                            {/* Botones de pánico — SIN isPaused */}
                            <SpacyPanicButtons
                                mode="free"
                                disabled={isSpacyThinking}
                                onHelp={handleHelpButton}
                            />

                            {/* INPUT (SIMPLIFICADO) */}
                            <div className="sef-input-area">
                                <div className="sef-chat-input-wrapper">
                                    <textarea
                                        maxLength={LIMITES.mensajeSpacy}
                                        ref={textareaRef}
                                        className="sef-chat-input"
                                        placeholder={
                                            isRecording
                                                ? (live.isLive ? "Te escucho..." : "Grabando... habla ahora...")
                                                : isTranscribing
                                                    ? "Transcribiendo..."
                                                    : "Escribe tu mensaje..."
                                        }
                                        /* Mientras habla ve el texto salir; al soltar vuelve a mandar
                                           `userInput`, que es lo que puede editar y lo que se envia. */
                                        value={isRecording && live.liveText ? `${userInput ? userInput + ' ' : ''}${live.liveText}` : userInput}
                                        onChange={(e) => { setUserInput(e.target.value); autoGrow(e.target); }}
                                        disabled={isSpacyThinking || isRecording || isTranscribing}
                                        style={getInputStyle()}
                                    />

                                    <div className="sef-input-icon-group">
                                        <button
                                            className={`sef-input-icon-btn ${isRecording ? 'listening' : ''}`}
                                            onClick={handleToggleListening}
                                            disabled={isSpacyThinking || isTranscribing}
                                            aria-label={isRecording ? "Detener grabación" : "Iniciar grabación"}
                                        >
                                            {isTranscribing ? <Loader className="spin-animation" size={18} /> : <Mic size={18} />}
                                            {isRecording && <div className="sef-recording-dot"></div>}
                                        </button>
                                        {userInput && (
                                            <button className="sef-input-icon-btn clear" onClick={() => { setUserInput(''); if (textareaRef.current) textareaRef.current.style.height = '40px'; }} aria-label="Limpiar texto">×</button>
                                        )}
                                        <button
                                            className="sef-input-icon-btn send"
                                            onClick={handleSendMessage}
                                            disabled={!userInput.trim() || isSpacyThinking || isRecording}
                                            aria-label="Enviar mensaje"
                                        >
                                            <Send size={18} />
                                        </button>
                                    </div>
                                </div>
                                {/* ── EL CONTADOR ──────────────────────────────────
                                    Va DEBAJO de la caja y no dentro. Dentro quedaba
                                    encima del microfono y del boton de enviar: se
                                    leia mal y ademas ensuciaba una zona que ya tiene
                                    tres controles. Aqui no compite con nada.

                                    Y se enseña siempre. La primera version aparecia
                                    al 80%, con el argumento de que "12 de 500" es
                                    ruido; el argumento era mio, no del alumno. Con
                                    la caja vacia el limite no se ve por ningun lado,
                                    y descubrirlo consiste en chocarse con el. */}
                                <div className="sef-contador-tope" style={{
                                    textAlign: 'right', padding: '4px 18px 0 0',
                                    fontSize: 11, fontWeight: 700, letterSpacing: '.02em',
                                    pointerEvents: 'none', transition: 'color .2s',
                                    color: userInput.length >= LIMITES.mensajeSpacy
                                        ? '#ef4444'
                                        : avisaDelTope(userInput.length, LIMITES.mensajeSpacy)
                                            ? '#f59e0b'
                                            : 'rgba(148,163,184,0.7)',
                                }}>
                                    {userInput.length}/{LIMITES.mensajeSpacy}
                                </div>

                                {isRecording && (
                                    <div className="sef-recording-status-area">
                                        <div className="sef-recording-indicator">
                                            <div className="sef-pulse-animation"></div>
                                            {/* El estado del oido en vivo se dice aqui, en una palabra.
                                                Sin esto, que el streaming falle es indistinguible de que
                                                funcione: en los dos casos la caja esta vacia. */}
                                            <span>
                                                Grabando audio... (Spanglish soportado)
                                                {live.isLive
                                                    ? ' · texto en vivo'
                                                    : (live.motivoSinVivo ? ` · ${live.motivoSinVivo}` : ' · conectando...')}
                                            </span>
                                        </div>
                                        <div className="sef-voice-suggestions">
                                            <small>💡 Habla con naturalidad. Toca el micrófono para terminar.</small>
                                        </div>
                                    </div>
                                )}

                                {isTranscribing && (
                                    <div className="sef-recording-status-area">
                                        <div className="sef-recording-indicator">
                                            <Loader className="spin-animation" size={14} />
                                            <span>Procesando tu voz con IA...</span>
                                        </div>
                                    </div>
                                )}

                                {voiceRecognitionError && (
                                    <div className="sef-voice-error">
                                        <AlertCircle size={14} /><span>{voiceRecognitionError}</span>
                                    </div>
                                )}

                                {/* El entrenador de voz. Solo sale despues de hablar y desaparece al
                                    enviar: es un plus, no un acompanante de la conversacion. */}
                                {!isRecording && !isTranscribing && (
                                    <VoiceCoachStrip
                                        metrics={voiceMetrics}
                                        transcript={rawTranscript}
                                        level={currentConfig?.user_level || user_level}
                                        onUseText={(texto) => {
                                            setUserInput(texto);
                                            if (textareaRef.current) setTimeout(() => autoGrow(textareaRef.current), 0);
                                        }}
                                    />
                                )}
                            </div>

                            {!sesionCerrada && conversation.length > 2 && (
                                <div className="sef-terminar-wrapper">
                                    <button type="button" className="sef-terminar-btn"
                                        data-prueba="terminar-sesion"
                                        onClick={() => { setSesionCerrada(true); launchConfetti(); }}>
                                        <Flag size={15} /> Terminar sesión
                                    </button>
                                </div>
                            )}

                            <div className="sef-fullscreen-button-wrapper">
                                <button
                                    className={`sef-fullscreen-btn ${isFullscreen ? 'active' : ''}`}
                                    onClick={handleToggleFullscreen}
                                    title={isFullscreen ? 'Salir del modo foco' : 'Modo foco: la clase a pantalla completa'}
                                >
                                    {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
                                    <span>{isFullscreen ? 'Salir' : 'Foco'}</span>
                                </button>
                            </div>
                        </>
                    )}

                </main>
            </div>
        </ExerciseTokensGuard>
    );
}

export default SpeakingExerciseFree;